import { useCallback, useEffect, useState } from 'react';

import { api } from './api';

interface State<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * Fetch-on-mount with an optional poll.
 *
 * The collector writes once a minute, so the dashboard refreshes on the same
 * cadence by default. Polling faster only re-reads rows nothing has changed;
 * polling slower makes a monitoring tool lie about how current it is.
 *
 * The in-flight generation counter is what stops a slow response for one server
 * landing after the user has already navigated to another.
 */
export function useApi<T>(path: string | null, pollMs = 60_000): State<T> & { reload: () => void } {
  const [state, setState] = useState<State<T>>({ data: null, error: null, loading: true });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!path) return;
    let live = true;

    const run = async (isRefresh: boolean) => {
      // A refresh keeps the old data on screen. Blanking the page every minute
      // makes a working dashboard look like it is failing.
      if (!isRefresh) setState((s) => ({ ...s, loading: true }));
      try {
        const data = await api<T>(path);
        if (live) setState({ data, error: null, loading: false });
      } catch (err) {
        if (live) {
          setState((s) => ({
            data: s.data,
            error: err instanceof Error ? err.message : 'Request failed',
            loading: false,
          }));
        }
      }
    };

    void run(false);
    if (!pollMs) return () => { live = false; };

    const timer = setInterval(() => void run(true), pollMs);
    return () => {
      live = false;
      clearInterval(timer);
    };
  }, [path, pollMs, nonce]);

  return { ...state, reload };
}
